import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Services
import { ReportingService } from 'src/app/core/services/reporting/reporting.service';
import { ReportJobService, ReportJob } from 'src/app/core/services/reporting/report-job.service';
import { ScheduledReportService } from 'src/app/core/services/reporting/scheduled-report.service';

// Models
import { DashboardStats } from './reporting-dashboard-mock-data/dashboard-stats-mock-data';
import { ScheduledReport } from '../scheduled/reporting-scheduled-mock-data/scheduled-reports-mock-data';

// Kendo Components
import { MetricCardComponent } from '../../../../../core/components/metric-card/metric-card.component';

/**
 * Dashboard Component
 * Shows overview of reporting activity, recent reports, and upcoming schedules
 */
@Component({
  selector: 'app-reporting-dashboard',
  standalone: true,
  imports: [CommonModule, MetricCardComponent],
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit {
  private reportingService = inject(ReportingService);
  private reportJobService = inject(ReportJobService);
  private scheduledReportService = inject(ScheduledReportService);

  // Dashboard data
  public stats = signal<DashboardStats | null>(null);
  public recentReports = signal<ReportJob[]>([]);
  public upcomingSchedules = signal<ScheduledReport[]>([]);
  public isLoading = signal<boolean>(true);

  ngOnInit(): void {
    this.loadDashboardData();
  }

  /**
   * Load all dashboard data
   */
  private loadDashboardData(): void {
    this.isLoading.set(true);

    // Load dashboard stats
    this.reportingService.getDashboardStats().subscribe({
      next: (stats) => {
        this.stats.set(stats);
      },
      error: (error) => {
        console.error('Error loading dashboard stats:', error);
      },
    });

    // Load recent reports (last 30 days)
    this.reportJobService.getRecentReportJobs(30).subscribe({
      next: (jobs) => {
        // Get last 5 most recent
        this.recentReports.set(jobs.slice(0, 5));
      },
      error: (error) => {
        console.error('Error loading recent reports:', error);
      },
    });

    // Load upcoming scheduled reports (next 7 days)
    this.scheduledReportService.getUpcomingScheduledReports(7).subscribe({
      next: (schedules) => {
        this.upcomingSchedules.set(schedules.slice(0, 5));
        this.isLoading.set(false);
      },
      error: (error) => {
        console.error('Error loading upcoming schedules:', error);
        this.isLoading.set(false);
      },
    });
  }

  /**
   * Download a completed report
   */
  public downloadReport(job: ReportJob, customerId?: number): void {
    if (job.status !== 'completed') {
      return;
    }

    this.reportJobService.downloadReport(job.id, customerId).subscribe({
      next: (response) => {
        window.open(response.download_url, '_blank');
      },
      error: (error) => {
        console.error('Error downloading report:', error);
      },
    });
  }

  /**
   * Retry a failed report
   */
  public retryReport(job: ReportJob): void {
    if (job.status !== 'failed') {
      return;
    }

    this.reportJobService.retryReportJob(job.id).subscribe({
      next: (newJob) => {
        console.log('Report retry initiated:', newJob);
        // Refresh dashboard data
        this.loadDashboardData();
      },
      error: (error) => {
        console.error('Error retrying report:', error);
      },
    });
  }

  /**
   * Format date for display
   */
  public formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get status badge class
   */
  public getStatusClass(status: string): string {
    const statusClasses: { [key: string]: string } = {
      completed: 'badge bg-success',
      processing: 'badge bg-primary',
      queued: 'badge bg-secondary',
      failed: 'badge bg-danger',
    };
    return statusClasses[status] || 'badge bg-secondary';
  }

  /**
   * Get status icon
   */
  public getStatusIcon(status: string): string {
    const statusIcons: { [key: string]: string } = {
      completed: '✓',
      processing: '⟳',
      queued: '⏱',
      failed: '✗',
    };
    return statusIcons[status] || '•';
  }
}
